import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';

export interface BeneficiaryOption {
  id: string;
  label: string;
  currencyCode: string | null;
}

/**
 * `BeneficiaryResponseDto` du CBS, qui repond en camelCase.
 *
 * Le nom affiche est `label` quand il existe, sinon la raison sociale :
 * c'est deja ce que fait `mapApiBeneficiary` pour la fiche client.
 */
const BeneficiaryRowSchema = z.object({
  beneficiaryId: z.coerce.string(),
  label: z.string().nullish(),
  companyName: z.string().nullish(),
  currencyCode: z.string().nullish(),
  accountNumber: z.string().nullish(),
});

/**
 * A client's registered beneficiaries, for picking the payee of a wire.
 *
 * A manual outgoing has to name one: `payment.beneficiary_id` is NOT NULL,
 * and typing a free-text name would either fail the insert or quietly add a
 * row to a list the compliance team reviews.
 */
export function useBeneficiaryOptions(clientId: number | undefined): {
  beneficiaries: BeneficiaryOption[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery<BeneficiaryOption[]>({
    queryKey: ['client-beneficiaries', clientId],
    enabled: clientId !== undefined,
    queryFn: async () => {
      const raw = await api.get<unknown[]>(`/api/clients/${clientId}/beneficiaries`);
      return z
        .array(BeneficiaryRowSchema)
        .parse(raw)
        .map((b) => {
          const name = b.label ?? b.companyName ?? '';
          return {
            id: b.beneficiaryId,
            label: b.accountNumber ? `${name} — ${b.accountNumber}` : name,
            currencyCode: b.currencyCode ?? null,
          };
        });
    },
    staleTime: 60_000,
  });

  // Une reponse qui ne colle pas au contrat ressemblait a « ce client n'a
  // aucun beneficiaire », et l'operateur cherchait un payee qui existe.
  return {
    beneficiaries: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
